import {
  WEBSOCKET_MESSAGE,
  WEBSOCKET_OPEN,
  WEBSOCKET_CONNECT,
  WEBSOCKET_CLOSED,
  DEFAULT_PREFIX,
  disconnect,
  connect,
} from '@giantmachines/redux-websocket';
import { send } from '@giantmachines/redux-websocket';
import { parseString } from 'xml2js';
import { roomSlice } from '../room/roomSlice';
import { cleanup as playerCleanup } from '../room/player';
import { cleanup as tableCleanup } from '../room/table';
import { settingsSlice, getBoardNo } from '../settings/settingsSlice';
import { gameSlice, getCurrentChars, getLocalPlayerNo, inGame } from '../game/gameSlice';
import { viewSlice } from '../view/viewSlice';
import { emptyBoard } from '../game/boardUtil';
import { batch } from 'react-redux';
import { DialogType } from '../../DialogHandler';
import { call } from '../../util/api';
import { getConnectionArgs } from '../room/roomSlice';
import { getID } from '../user/userSlice';
import { connectionSlice, connectionState, getLastMessageSent, getDisplayState } from '../connection/connectionSlice';
import { getTimer } from '../../util/timer';

const cleanChar = (char) => {
  if (char.joker && char.data === '.') {
    return {data: '', joker: true};
  }
  return char;
}

let leftOverBuffer = new ArrayBuffer();

const websocketController = connectArgs => store => next => action => {
  const dispatch = store.dispatch;
  switch (action.type) {
    case 'action/joinroom': {
      const payload = action.payload;
      const state = store.getState();
      if (getID(state) === null) {
        dispatch(viewSlice.actions.addDialog({type: DialogType.LOGIN, args: {roomID: payload.id}}));
        break;
      }

      batch(() => {
        dispatch(roomSlice.actions.reset());
        call({func: 'join_room', args: { roomid: payload.id }}, store);
        dispatch(roomSlice.actions.setID(payload.id));
        dispatch(viewSlice.actions.setSelected({tab: 'room'}));

        // We are fetching information so we can connect so set state to connecting
        dispatch(connectionSlice.actions.setDisplayState(connectionState.CONNECTING));
        if (!(payload.reconnect ?? null)) {
          dispatch(connectionSlice.actions.resetDisconnectCount());
        }
      });
      break;
    }
    case 'action/login': {
      const payload = action.payload;
      call({func: 'login', args: { username: payload.username, password: payload.password}, payload: { roomID: payload.roomID }}, store);
      break;
    }
    case 'action/start': {
      const payload = action.payload;
      const obj = {
        'STARTGAME': {
          $: {
            'ID': payload.id.toString(),
          }
        }
      };
      batch(() => {
        dispatch(settingsSlice.actions.setBoardNo(null));
        dispatch(send(obj));
      });
      break;
    }
    case 'action/leave': {
      const payload = action.payload;
      const obj = {
        'PARTGAME': {
          $: {
            'ID': payload.id.toString(),
          }
        }
      };
      batch(() => {
        dispatch(settingsSlice.actions.setBoardNo(null));
        dispatch(viewSlice.actions.setSelected({tab: 'room'}));
        dispatch(send(obj));
      });
      break;
    }
    case 'action/invite': {
      const payload = action.payload;
      const obj = {
        'INVITE': {
          $: {
            'ID': payload.id.toString(),
          },
        }
      };
      dispatch(send(obj));
      break;
    }
    case 'action/kick': {
      const payload = action.payload;
      const obj = {
        'KICK': {
          $: {
            'ID': payload.id.toString(),
          },
        }
      };
      dispatch(send(obj));
      break;
    }
    case 'action/abort': {
      const obj = {
        'ABORTGAME': {}
      };
      batch(() => {
        dispatch(settingsSlice.actions.setBoardNo(null));
        dispatch(viewSlice.actions.closeDialog(DialogType.CREATE_GAME));
        dispatch(send(obj));
      });
      break;
    }
    case 'action/spy': {
      const payload = action.payload;
      const state = store.getState();
      const isInGame = inGame(state);
      batch(() => {
        if (isInGame) {
          dispatch(send({'QUITGAME': {} }));
          dispatch(gameSlice.actions.setInGame(false));
        }
        dispatch(viewSlice.actions.setSelected({tab: 'game'}));
        dispatch(send({'WATCHGAME': { $: { 'ID': payload.id.toString() } } }));
      });
      break;
    }

    case 'action/join': {
      const payload = action.payload;
      const state = store.getState();
      const isInGame = inGame(state);
      batch(() => {
        if (isInGame) {
          dispatch(send({'QUITGAME': {} }));
          dispatch(gameSlice.actions.setInGame(false));
        }
        dispatch(viewSlice.actions.setSelected({tab: 'game'}));
        dispatch(send({'JOINGAME': { $: { 'ID': payload.id.toString() } } }));
      });
      break;
    }

    case 'action/precreate': {
      const state = store.getState();
      const isInGame = inGame(state);
      batch(() => {
        if (isInGame) {
          dispatch(send({'QUITGAME': {} }));
          dispatch(gameSlice.actions.setInGame(false));
        }
        dispatch(viewSlice.actions.addDialog({type: DialogType.CREATE_GAME}));
        dispatch(send({'CREATEREQUEST': {} }));
      });
      break;
    }

    case 'action/giveup': {
      dispatch(send({'GIVEUP': {} }));
      break;
    }

    case 'action/create': {
      const payload = action.payload;
      const state = store.getState();
      const boardNo = getBoardNo(state);
      const obj = {
        'HOSTGAME': {
          $: {
            'ID': boardNo.toString(),
            'INVITE': payload.inviteOnly ? 'TRUE' : 'FALSE',
            'RANK': payload.ranked ? 'TRUE' : 'FALSE',
            'NUMPLAYERS': payload.players.toString(),
            'GAMETYPE': payload.gameType.toString(),
            'BOARD': payload.boardType.toString(),
            'NAME': payload.name,
          }
        }
      };
      batch(() => {
        dispatch(viewSlice.actions.closeDialog(DialogType.CREATE_GAME));
        dispatch(viewSlice.actions.setSelected({tab: 'game'}));
        dispatch(send(obj));
      });
      break;
    }
    case 'action/gameMessage': {
      const payload = action.payload;
      const obj = {
        'GAMEMSG': {
          $: {
            'DATA': payload.data,
          }
        }
      };
      dispatch(send(obj));
      break;
    }
    case 'action/message': {
      const payload = action.payload;
      const obj = {
        'MSG': {
          $: {
            'DEST': payload.dest,
            'DATA': payload.data,
          }
        }
      };
      dispatch(send(obj));
      break;
    }
    case 'action/playTiles': {
      const state = store.getState();
      const currentChars = getCurrentChars(state);

      const emptyJokers = currentChars.filter(c => c.char.joker && c.char.data === '');
      if (emptyJokers.length > 0) {
        dispatch(gameSlice.actions.tryToPlayEmptyJoker());
        break;
      }

      const obj = {
        'CHARS': {
          'LETTER': currentChars.map(c => {
            return {
              '$': {
                'Y': c.y.toString(),
                'X': c.x.toString(),
                'DATA': c.char.data,
                'JOKER': c.char.joker ? 'TRUE' : 'FALSE',
              }
            };
          }),
        }
      };

      batch(() => {
        dispatch(gameSlice.actions.wait(true));
        dispatch(send(obj));
      });
      break;
    }

    case 'action/swapTiles': {
      const state = store.getState();
      const currentChars = getCurrentChars(state);

      const obj = {
        'GIMME': {
          'LETTER': currentChars.map(c => {
            return {
              '$': {
                'DATA': c.char.data,
                'JOKER': c.char.joker ? 'TRUE' : 'FALSE',
              }
            };
          }),
        }
      };

      batch(() => {
        dispatch(gameSlice.actions.wait(true));
        dispatch(send(obj));
      });
      break;
    }

    case 'action/pass': {
      const obj = {
        'PASS': {}
      };

      batch(() => {
        dispatch(gameSlice.actions.wait(true));
        dispatch(send(obj));
      });
      break;
    }

    case 'action/pause': {
      const obj = {
        'PAUSE': {
          '$': {
            'TYPE': 'ON',
          }
        }
      };
      dispatch(send(obj));
      break;
    }

    case 'action/acceptpause': {
      const obj = {
        'PAUSE': {
          '$': {
            'ACCEPT': 'YES',
          }
        }
      };
      dispatch(send(obj));
      break;
    }

    case 'action/quitgame': {
      batch(() => {
        dispatch(send({'QUITGAME': {} }));
        dispatch(gameSlice.actions.setInGame(false));
        dispatch(viewSlice.actions.setSelected({tab: 'room'}));
      });
      break;
    }

    case 'action/auto/tick': {
      const payload = action.payload;
      switch (payload) {
        case 'game': {
          dispatch(gameSlice.actions.tick());
          break;
        }
        case 'connection': {
          const state = store.getState();
          const lastMessage = getLastMessageSent(state);
          const diff = Date.now() - lastMessage;
          if (diff > 10100) {
            // We haven't gotten a message in the last 10 seconds and we sent
            // a ping 5 seconds ago
            dispatch(disconnect());
          } else if (diff > 5100) {
            // If we haven't gotten a message in the last 5 seconds send a ping
            // to the server
            const obj = {
              'PING': {}
            }
            dispatch(send(obj));
          }
          break;
        }
        default:
          break;
      }
      break;
    }

    case 'api/request': {
      call(action.payload, store);
      break;
    }

    case 'api/response': {
      break;
    }

    case `${DEFAULT_PREFIX}::${WEBSOCKET_CONNECT}`: {
      dispatch(connectionSlice.actions.setDisplayState(connectionState.CONNECTING));
      break;
    }

    case `${DEFAULT_PREFIX}::${WEBSOCKET_CLOSED}`: {
      const state = store.getState();
      getTimer().cancel('connection');

      const connectionArgs = getConnectionArgs(state);
      const s = getDisplayState(state);
      if (s === connectionState.CONNECTING && connectionArgs !== null) {
        dispatch(connect(connectionArgs.url, ['binary']));
      } else  {
        dispatch(connectionSlice.actions.setDisplayState(connectionState.DISCONNECTED));
      }
      break;
    }

    case `${DEFAULT_PREFIX}::${WEBSOCKET_OPEN}`: {
      const state = store.getState();
      const userID = getID(state);
      const connectionArgs = getConnectionArgs(state);

      getTimer().start('connection', 2000);

      const obj = {
        'LOGIN': {
          $: {
            'ID': userID,
            'VER': '1.1',
            'TIME': connectionArgs.time.toString(),
            'AUTHKEY': connectionArgs.authkey,
          }
        }
      };
    
      batch(() => {
        dispatch(connectionSlice.actions.setDisplayState(connectionState.CONNECTED));
        dispatch(send(obj));
      });
      break;
    }

    case `${DEFAULT_PREFIX}::${WEBSOCKET_MESSAGE}`: {
      dispatch(connectionSlice.actions.setLastMessageSent());

      const makeArr = (o) => {
        return Array.isArray(o) ? o : [o];
      };

      const decoder = new TextDecoder("iso-8859-1");
      let buffer = action.payload.message;

      if (leftOverBuffer.byteLength > 0) {
        let newBuffer = new Uint8Array(leftOverBuffer.byteLength + buffer.byteLength);
        newBuffer.set(new Uint8Array(leftOverBuffer), 0);
        newBuffer.set(new Uint8Array(buffer), leftOverBuffer.length);
        buffer = newBuffer.buffer;
        leftOverBuffer = new ArrayBuffer();
      }

      if (connectArgs.log === 2) {
        console.log(decoder.decode(buffer));
      }
      let uint8array = new Uint8Array(buffer);
      let start = 0
      let end = uint8array.findIndex((v) => v === 0);
      while (end !== -1) {
        const xmlStr = decoder.decode(buffer.slice(start, start + end));
        if (connectArgs.log === 2) {
          console.log(xmlStr);
        }
        let xml;
        parseString(xmlStr, (e, r) => { if (e !== null && connectArgs.log === 2) { console.log(e); } xml = r; });
        if (connectArgs.log === 2) {
          console.log(xml);
        }
        for (const prop of Object.getOwnPropertyNames(xml)) {
          switch (prop) {
            case 'USERLIST': {
              const actions = makeArr(xml.USERLIST.USER).map(u => {
                const attr = u['$'];
                return roomSlice.actions.addPlayer(playerCleanup({
                  id: attr.ID,
                  name: attr.NAME,
                  rating: attr.RATING,
                  premium: attr.PREMIUM,
                  lvl: attr.LVL,
                  table: attr.TABLE,
                  local: attr.LOCAL,
                }));
              });

              batch(() => {
                actions.map(a => dispatch(a));
              });
              break;
            }
            case 'GAME_TYPES': {
              let gameTypes = makeArr(xml.GAME_TYPES.GAME_TYPE).map((t) => {
                const attr = t['$'];
                return {id: parseInt(attr.ID), name: attr.NAME, players: parseInt(attr.PLAYERS)};
              });
              dispatch(settingsSlice.actions.setGameTypes(gameTypes));
              break;
            }
            case 'BOARD_TYPES': {
              let boardTypes = makeArr(xml.BOARD_TYPES.BOARD_TYPE).map((b) => {
                const attr = b['$'];
                return {id: parseInt(attr.ID), name: attr.NAME};
              });
              dispatch(settingsSlice.actions.setBoardTypes(boardTypes));
              break;
            }
            case 'POSTINIT': {
              dispatch(roomSlice.actions.setLoaded(true));
              break;
            }
            case 'JOIN': {
              const attr = xml.JOIN['$'];
              dispatch(roomSlice.actions.addPlayer(playerCleanup({
                id: attr.ID,
                name: attr.NAME,
                rating: attr.RATING,
                premium: attr.PREMIUM,
                lvl: attr.LVL,
                table: attr.TABLE,
              })));
              break;
            }
            case 'PART': {
              const attr = xml.PART['$'];
              dispatch(roomSlice.actions.removePlayer(playerCleanup({id: attr.ID})));
              break;
            }
            case 'UPDATE': {
              let playerUpdates = [];

              const attr = xml.UPDATE['$'];
              if (attr !== undefined) {
                playerUpdates.push(roomSlice.actions.updatePlayer(playerCleanup({id: attr.ID, table: attr.TABLE})));
              } else {
                for (const u of makeArr(xml.UPDATE.USER)) {
                  const attr = u['$'];
                  playerUpdates.push(roomSlice.actions.updatePlayer(playerCleanup({id: attr.ID, rating: attr.RATING, table: "-1", })));
                }
              }

              batch(() => {
                playerUpdates.map(u => dispatch(u));
              });
              break;
            }
            case 'START_ALFAPET_CLIENT': {
              const obj = {
                'GAMELOGIN': {}
              };
        
              batch(() => {
                dispatch(gameSlice.actions.setInGame(true));
                dispatch(send(obj));
              });
              break;
            }
            case 'START_SPYING': {
              const obj = {
                'GAMELOGIN': {}
              };
        
              batch(() => {
                dispatch(gameSlice.actions.setInGame(true));
                dispatch(send(obj));
              });
              break;
            }
            case 'ASK_GAME_RECONNECT': {
              batch(() => {
                dispatch(viewSlice.actions.setSelected({tab: 'game'}));
                dispatch(gameSlice.actions.setInGame(true));
              });

              const obj = {
                'RESUME': {
                  '$': {
                    'ANSWER': '1',
                  }
                }
              };
              dispatch(send(obj));

              const obj2 = {
                'GAMELOGIN': {}
              };
              dispatch(send(obj2));
              break;
            }
            case 'HOSTGAME': {
              const attr = xml.HOSTGAME['$'];
              const table = tableCleanup({
                id: attr.ID,
                name: attr.NAME,
                rank: attr.RANK,
                invite: attr.INVITE,
                gametype: attr.GAMETYPE,
                boardtype: attr.BOARD,
                numplayers: attr.NUMPLAYERS,
                hostid: attr.HOSTID,
                premium: attr.PREMIUM,
                started: attr.NUMCON === '-1',
                players: [],
                invited: false,
              });
              dispatch(roomSlice.actions.addTable(table));
              break;
            }
            case 'NEWHOST': {
              const attr = xml.NEWHOST['$'];
              dispatch(roomSlice.actions.updateTable(tableCleanup({
                id: attr.GAMEID,
                hostid: attr.HOSTID,
              })));
              break;
            }
            case 'DELETEGAME': {
              const attr = xml.DELETEGAME['$'];
              dispatch(roomSlice.actions.removeTable(tableCleanup({id: attr.ID})));
              break;
            }
            case 'GAVEUP': {
              const attr = xml.GAVEUP['$'];
              dispatch(gameSlice.actions.gaveup({no: parseInt(attr.NO)}));
              break;
            }
            case 'ENDGAME': {
              const attr = xml.ENDGAME['$'];
              let payload = {
                dropped: (attr !== undefined && attr.DROPPED !== undefined) ? parseInt(attr.DROPPED) : null,
                winner: (attr !== undefined && attr.WINNER !== undefined) ? parseInt(attr.WINNER) : null,
                players: makeArr(xml.ENDGAME.PLAYER).map((p) => {
                  const attr = p['$'];
                  const charScore = (attr.CHARSCOREMINUS && parseInt(attr.CHARSCOREMINUS) > 0) ? -parseInt(attr.CHARSCOREMINUS) : parseInt(attr.CHARSCOREPLUS);
                  return {
                    no: parseInt(attr.NO),
                    score: parseInt(attr.SCORE),
                    timeScore: attr.TIMESCORE === undefined ? null : parseInt(attr.TIMESCORE),
                    charScore: charScore,
                    rating: attr.RATING === undefined ? null : parseInt(attr.RATING),
                    oldRating: attr.OLDRATING === undefined ? null : parseInt(attr.OLDRATING),
                  };
                }),
              };
              dispatch(gameSlice.actions.endGame(payload));
              break;
            }
            case 'GAME_ENDED_ABRUPT': {
              dispatch(gameSlice.actions.gameEndedAbrupt());
              break;
            }
            case 'INC': {
              // Ignore
              break;
            }
            case 'DEC': {
              // Ignore
              break;
            }
            case 'JOINGAME': {
              // Ignore
              break;
            }
            case 'START': {
              const attr = xml.START['$'];
              dispatch(roomSlice.actions.updateTable({id: attr.ID, started: true}));
              break;
            }
            case 'BOARD': {
              const boardAttr = xml.BOARD['$'];
              let board = emptyBoard(parseInt(boardAttr.HEIGHT), parseInt(boardAttr.WIDTH));
              for (const b of makeArr(xml.BOARD.BRICK)) {
                const attr = b['$'];
                board[parseInt(attr.Y)][parseInt(attr.X)] = parseInt(attr.DATA);
              }
              dispatch(gameSlice.actions.setBoard(board));
              break;
            }
            case 'CHARS': {
              let chars = makeArr(xml.CHARS.LETTER).map((l) => {
                const attr = l['$'];
                return {pos: {type: 'board', y: parseInt(attr.Y), x: parseInt(attr.X)}, char: cleanChar({data: attr.DATA, joker: attr.JOKER === 'TRUE'})};
              });
              dispatch(gameSlice.actions.playedChars(chars));
              break;
            }
            case 'DEFLETTERS': {
              let chars = makeArr(xml.DEFLETTERS.DEFLETTER).map((l) => {
                const attr = l['$'];
                return {char: attr.LETTER, points: parseInt(attr.VALUE)};
              });
              dispatch(gameSlice.actions.setDefLetters(chars));
              break;
            }
            case 'LETTERS': {
              let chars = makeArr(xml.LETTERS.LETTER).map((l) => {
                const attr = l['$'];
                return {char: cleanChar({data: attr.DATA, joker: (attr.JOKER === 'TRUE')})};
              });
              dispatch(gameSlice.actions.newTiles(chars));
              break;
            }
            case 'SPY_DATA_SENT':
            case 'START_DATA_SENT': {
              dispatch(gameSlice.actions.startDataSent());
              break;
            }
            case 'WORDS': {
              const attr = xml.WORDS['$'];

              let payload = {
                no: parseInt(attr.NO),
                ok: attr.OK === 'TRUE',
                triesLeft: (attr.TRIESLEFT !== undefined) ? parseInt(attr.TRIESLEFT) : null,
                words: makeArr(xml.WORDS.WORD).map((w) => {
                  const attr = w['$'];
                  return {word: attr.DATA, points: parseInt(attr.POINTS)};
                }),
              };
              dispatch(gameSlice.actions.playedWords(payload));
              break;
            }
            case 'BOGUS_CHARS_GIVEN':
            case 'NOT_ALLOWED_TO_SWAP':
            case 'OCCUPIED_SQUARE':
            case 'START_IN_THE_MIDDLE':
            case 'ONE_CHAR_WORD_NOT_ALLOWED':
            case 'NOT_ATTACHED_CHAR':
            case 'NOT_IN_LINE':
            case 'WE_GOT_GAPS':
            case 'PAUSE_IN_PROGRESS': {
              dispatch(gameSlice.actions.badPlay(prop));
              break;
            }
            case 'SWAPPED': {
              const attr = xml.SWAPPED['$'];
              dispatch(gameSlice.actions.swapped({no: parseInt(attr.NO), number: parseInt(attr.NUMBER)}));
              break;
            }
            case 'PASS': {
              const attr = xml.PASS['$'];
              dispatch(gameSlice.actions.passed({no: parseInt(attr.NO)}));
              break;
            }
            case 'PAUSE': {
              const attr = xml.PAUSE['$'];
              const no = attr.FROMID ? parseInt(attr.FROMID) : null;
              if (no !== null) {
                const request = attr.TYPE === 'REQUEST';
                const choice = attr.CHOICE === 'ON';
                const state = store.getState();
                const localPlayerNo = getLocalPlayerNo(state);
                if (request && no !== localPlayerNo) {
                  dispatch(viewSlice.actions.addDialog({type: DialogType.PAUSE, args: { choice }}));
                }
                dispatch(gameSlice.actions.pause({no, request, choice, accept: attr.ACCEPT === 'YES'}));
              } else {
                const paused = attr.NEWSTATE ? attr.NEWSTATE === 'ON' : null;
                if (paused !== null) {
                  dispatch(gameSlice.actions.pauseState({paused}));
                }
              }
              break;
            }
            case 'MISSED_TRIES': {
              const attr = xml.MISSED_TRIES['$'];
              dispatch(gameSlice.actions.missedTries({no: parseInt(attr.NO), number: parseInt(attr.NUMBER)}));
              break;
            }
            case 'DROP_NOT_FIN': {
              const attr = xml.DROP_NOT_FIN['$'];
              dispatch(gameSlice.actions.dropNotFin({no: parseInt(attr.NO)}));
              break;
            }
            case 'JOIN_AFT_DROP': {
              const attr = xml.JOIN_AFT_DROP['$'];
              dispatch(gameSlice.actions.joinAfterDrop({no: parseInt(attr.NO)}));
              break;
            }
            case 'TIME_EXPIRED': {
              const attr = xml.TIME_EXPIRED['$'];
              dispatch(gameSlice.actions.timeExpired({no: parseInt(attr.NO)}));
              break;
            }
            case 'GAMEMSG': {
              const attr = xml.GAMEMSG['$'];
              dispatch(gameSlice.actions.message({no: parseInt(attr.NO), data: attr.DATA}));
              break;
            }
            case 'RULLNING': {
              const attr = xml.RULLNING['$'];
              dispatch(gameSlice.actions.rolled({no: parseInt(attr.NO), points: parseInt(attr.POINTS)}));
              break;
            }
            case 'PLAYERLIST': {
              const players = makeArr(xml.PLAYERLIST.PLAYER).map((l) => {
                const attr = l['$'];
                return {id: parseInt(attr.ID), no: parseInt(attr.NO), name: attr.NAME, rating: parseInt(attr.RATING), local: attr.LOCAL === 'TRUE', points: 0, tilesLeft: null, turnTimeLeft: null, totalTimeLeft: null};
              });

              dispatch(gameSlice.actions.setPlayers(players));
              break;
            }
            case 'SPY': {
              const attr = xml.SPY['$'];
              dispatch(gameSlice.actions.spyJoin({name: attr.NAME}));
              break;
            }
            case 'SPYPART': {
              const attr = xml.SPYPART['$'];
              dispatch(gameSlice.actions.spyPart({name: attr.NAME}));
              break;
            }
            case 'TURN': {
              const attr = xml.TURN['$'];
              const turn = {
                no: parseInt(attr.NO),
                left: parseInt(attr.LEFT),
                bleft: parseInt(attr.BLEFT),
                turnTime: parseInt(attr.TURNTIME),
                totalTime: parseInt(attr.TOTALTIME),
              };
              dispatch(gameSlice.actions.turn(turn));
              break;
            }
            case 'TIMESYNCH': {
              const attr = xml.TIMESYNCH['$'];
              const timeSync = {
                no: parseInt(attr.NO),
                turnTime: parseInt(attr.TIME),
                totalTime: parseInt(attr.TOTALTIME),
              };
              dispatch(gameSlice.actions.timeSync(timeSync));
              break;
            }
            case 'CREATECONFIRM': {
              const attr = xml.CREATECONFIRM['$'];
              const no = parseInt(attr.NO);
              dispatch(settingsSlice.actions.setBoardNo(no));
              break;
            }
            case 'RESTART_IN_PROGRESS': {
              dispatch(roomSlice.actions.addRestartInProgress());
              break;
            }
            case 'INVITED': {
              const attr = xml.INVITED['$'];
              dispatch(roomSlice.actions.invited({tableID: parseInt(attr.GAMEID), playerID: parseInt(attr.USERID)}));
              break;
            }
            case 'INVITE': {
              const attr = xml.INVITE['$'];
              dispatch(roomSlice.actions.invite({tableID: parseInt(attr.GAMEID), playerID: parseInt(attr.USERID)}));
              break;
            }
            case 'KICKED': {
              dispatch(viewSlice.actions.setSelected({tab: 'room'}));
              break;
            }
            case 'USER_ALREADY_INVITED': {
              dispatch(roomSlice.actions.alreadyInvited());
              break;
            }
            case 'MSG': {
              const attr = xml.MSG['$'];
              switch (attr.DEST) {
                case 'LOBBY': {
                  dispatch(roomSlice.actions.addMessage({playerID: parseInt(attr.ID), data: attr.DATA}));
                  break;
                }
                case 'GAME': {
                  dispatch(roomSlice.actions.addMessage({playerID: parseInt(attr.ID), data: attr.DATA, tableID: parseInt(attr.DESTID)}));
                  break;
                }
                default:
                  throw Error('Unknown MSG DEST '+attr.DEST);
              } 
              break;
            }
            case 'ROLL': {
              const attr = xml.ROLL['$'];
              dispatch(roomSlice.actions.addRoll({playerID: parseInt(attr.ID), points: parseInt(attr.SCORE), word: attr.WORD}));
              break;
            }
            default:
              break;
          }
        }
        start += end + 1;
        uint8array = uint8array.slice(end+1);
        end = uint8array.findIndex((v) => v === 0);
      }

      if (uint8array.length > 0) {
        leftOverBuffer = uint8array;
      }
      break;
    }
    default:
      break;
  }

  return next(action)
};

export default websocketController;
